
import { computed, ComputedRef, defineComponent, ref } from 'vue'
// Router
import { useRouter, type Router } from 'vue2-helpers/vue-router'
// Components
import PaymentMethodHeader from '@/components/PaymentMethod/PaymentMethodHeader.vue'
import PrimaryButton from '@/components/Buttons/PrimaryButton.vue'
// Constants
import { PAYMENT_PROVIDER_ENUM } from '@/types/constantsTypes'
// Store
import { createVuexHelpers } from 'vue2-helpers'
// Types
import { CreditCard, EventTicketMeta } from '@/types/entitiesTypes'
import { GetCart, UpdateCart, OpenTab } from '@/types/storeTypes'

export default defineComponent({
	name: 'PaymentMethod',

	components: {
		CreditCardModalMagensa: () =>
			import('@/components/modals/CreditCardModalMagensa.vue'),
		PrimaryButton,
		CardConfirmation: () =>
			import('@/components/PaymentMethod/CardConfirmation.vue'),
		PaymentMethodHeader
	},

	setup() {
		// Router
		const router: Router = useRouter()

		// Refs
		const anotherMethodSelected = ref(false)
		const magensaLoading = ref(false)
		const cardConfirmationLoading = ref(false)

		// Store
		const { useGetters, useActions, useState } = createVuexHelpers()
		const {
			isCartRecreated,
			cartId
		}: {
			isCartRecreated: ComputedRef<boolean>
			cartId: ComputedRef<string>
		} = useState('cart', {
			isCartRecreated: 'isCartRecreated',
			cartId: 'id'
		})
		const {
			creditCards
		}: {
			creditCards: ComputedRef<CreditCard[]>
		} = useState('payment', ['creditCards'])

		const {
			isTabOpened
		}: {
			isTabOpened: ComputedRef<boolean>
		} = useGetters('cart', ['isTabOpened'])

		const {
			eventTicketMeta
		}: {
			eventTicketMeta: ComputedRef<EventTicketMeta>
		} = useGetters('events', ['eventTicketMeta'])
		const {
			caPaymentProvider
		}: {
			caPaymentProvider: ComputedRef<PAYMENT_PROVIDER_ENUM>
		} = useGetters('venue', ['caPaymentProvider'])

		const {
			getCart,
			updateCart,
			openTab
		}: {
			getCart: GetCart
			updateCart: UpdateCart
			openTab: OpenTab
		} = useActions('cart', ['getCart', 'updateCart', 'openTab'])

		// Computed
		const isMagensa = computed(
			() => caPaymentProvider.value === PAYMENT_PROVIDER_ENUM.MAGENSA
		)

		const creditCardExpiryErrorsLeft = computed(
			() => eventTicketMeta.value.creditCardExpiryErrorsLeft
		)

		const isCardAvailable = computed(
			() => eventTicketMeta.value.isTokenReusable
		)

		const isConfirmationFormAvailable = computed(
			() => creditCardExpiryErrorsLeft.value && isCardAvailable.value
		)

		const isConfirmationErrorVisible = computed(
			() => !creditCardExpiryErrorsLeft.value && isCardAvailable.value
		)

		// Methods
		const anotherMethodsHandle = (): void => {
			anotherMethodSelected.value = true
		}

		const checkCartExpiration = async (): Promise<boolean> => {
			await getCart(true)

			if (isCartRecreated.value) {
				magensaLoading.value = false
				cardConfirmationLoading.value = false

				return true
			}

			return false
		}

		const checkOpenTab = async (): Promise<boolean> => {
			if (isTabOpened.value) {
				await router.push({
					name: 'check'
				})

				return true
			}

			return false
		}

		const callOpenTab = async (): Promise<void> => {
			try {
				await openTab({ cartId: cartId.value })
			} catch (error) {
			} finally {
				magensaLoading.value = false
				cardConfirmationLoading.value = false
			}
		}

		const creditCardAdded = async (creditCard: CreditCard): Promise<void> => {
			magensaLoading.value = true

			const isCartExpired = await checkCartExpiration()

			if (isCartExpired) return

			const isTabOpen = await checkOpenTab()

			if (isTabOpen) return

			try {
				await updateCart({ creditCardId: creditCard.id })
			} catch (e) {
				magensaLoading.value = false

				return
			}

			await callOpenTab()
		}

		const onCardConfirmed = async (): Promise<void> => {
			cardConfirmationLoading.value = true
			const lastCreditCardId = creditCards.value &&
				creditCards.value[creditCards.value.length - 1] &&
				creditCards.value[creditCards.value.length - 1].id

			if (!lastCreditCardId) return

			const isCartExpired = await checkCartExpiration()

			if (isCartExpired) return

			const isTabOpen = await checkOpenTab()

			if (isTabOpen) return

			try {
				await updateCart({ creditCardId: lastCreditCardId })
			} catch (e) {
				cardConfirmationLoading.value = false

				return
			}

			await callOpenTab()
		}

		const setCardConfirmationLoading = (value: boolean): void => {
			cardConfirmationLoading.value = value
		}

		return {
			anotherMethodSelected,
			isMagensa,
			isConfirmationFormAvailable,
			isConfirmationErrorVisible,
			magensaLoading,
			cardConfirmationLoading,
			creditCardAdded,
			anotherMethodsHandle,
			onCardConfirmed,
			setCardConfirmationLoading
		}
	}
})
